<script setup lang="ts">
/**
 * The hover card of the kit's charts: a heading (the category) and one row per
 * series, value first. The anchor `x`/`y` is a point inside the chart's own box
 * (`position: relative` there); the card sits beside it, on the anchor's left
 * once it passes the middle.
 *
 * The card is a manual popover in the top layer, like the kit's `Tooltip`: a
 * panel around the chart clips anything inside it, and a chart with many series
 * is taller than its box. Only the window bounds it, and a list too tall even
 * for the window goes into several columns, since the card takes no pointer to
 * scroll with.
 *
 * Pure presentation — the chart decides what is hovered. Values in here are
 * also reachable without hovering (axis, legend, accessible name), so the
 * card is hidden from assistive technology.
 */
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { useFloatingPanel } from '../Popover/useFloatingPanel';
import { chartTooltipColumns } from './tooltipColumns';

export interface NxChartTooltipRow {
  key: string | number;
  label: string;
  value: string;
  color?: string;
  /** Key mark mirroring the chart's mark: a short stroke for lines, a square for bars. */
  shape?: 'line' | 'rect';
  /** Dims the row, e.g. a series with no value at this point. */
  muted?: boolean;
}

const props = withDefaults(
  defineProps<{
    /** Anchor in px from the chart box's left/top edge. */
    x: number;
    y: number;
    /** Width of the chart box, for flipping. */
    boxWidth: number;
    title?: string;
    rows?: readonly NxChartTooltipRow[];
    /** A summary line under the rows („Summe 1.240“). */
    footer?: string;
  }>(),
  { title: undefined, rows: () => [], footer: undefined }
);

/** The card's distance from the window's edge. */
const WINDOW_PADDING = 8;

const anchor = ref<HTMLElement | null>(null);
const card = ref<HTMLElement | null>(null);
const list = ref<HTMLElement | null>(null);
const columns = ref(1);

const anchorStyle = computed(() => ({ left: `${props.x}px`, top: `${props.y}px` }));

// Inside a modal dialog or a popover the card goes in with the chart: both are
// in the top layer, a card in <body> would open behind them.
const target = computed<HTMLElement | 'body'>(
  () => anchor.value?.closest<HTMLElement>('dialog, [popover]') ?? 'body'
);

const { floatingStyles, update } = useFloatingPanel(anchor, card, {
  placement: () => (props.x > props.boxWidth / 2 ? 'left' : 'right'),
  padding: WINDOW_PADDING,
});

const rowColumns = computed(() => {
  const perColumn = Math.ceil(props.rows.length / columns.value);
  return Array.from({ length: columns.value }, (_, i) =>
    props.rows.slice(i * perColumn, (i + 1) * perColumn)
  ).filter((rows) => rows.length > 0);
});

function measure() {
  if (!card.value || !list.value) return;
  columns.value = chartTooltipColumns({
    rows: props.rows.length,
    listHeight: list.value.offsetHeight,
    // From the DOM: a second call can come before the last result is rendered.
    listRows: list.value.firstElementChild?.childElementCount ?? 0,
    otherHeight: card.value.offsetHeight - list.value.offsetHeight,
    windowHeight: window.innerHeight - 2 * WINDOW_PADDING,
  });
}

watch(card, (el) => {
  if (!el) return;
  if ('showPopover' in el) {
    try {
      el.showPopover();
    } catch {
      /* already shown, or the element left the document */
    }
  }
  measure();
});

// A move to another point changes the rows and the anchor under a card already up.
watch(
  () => [props.x, props.y, props.rows] as const,
  () => {
    columns.value = 1;
    void nextTick(() => {
      measure();
      void nextTick(update);
    });
  }
);
window.addEventListener('resize', measure);
onBeforeUnmount(() => window.removeEventListener('resize', measure));
</script>

<template>
  <span ref="anchor" class="nx-chart-tooltip-anchor" :style="anchorStyle" aria-hidden="true" />
  <Teleport v-if="anchor" :to="target">
    <div
      ref="card"
      popover="manual"
      class="nx-chart-tooltip"
      :class="{ 'is-columns': rowColumns.length > 1 }"
      :style="floatingStyles"
      aria-hidden="true"
      data-testid="nx-chart-tooltip"
    >
      <div v-if="props.title" class="nx-chart-tooltip__title">{{ props.title }}</div>
      <div v-if="props.rows.length" ref="list" class="nx-chart-tooltip__columns">
        <ul v-for="(rows, index) in rowColumns" :key="index" class="nx-chart-tooltip__rows">
          <li
            v-for="row in rows"
            :key="row.key"
            class="nx-chart-tooltip__row"
            :class="{ 'is-muted': row.muted }"
          >
            <span
              v-if="row.color"
              class="nx-chart-tooltip__key"
              :class="`is-${row.shape ?? 'rect'}`"
              :style="{ background: row.color }"
            />
            <span class="nx-chart-tooltip__value">{{ row.value }}</span>
            <span class="nx-chart-tooltip__label">{{ row.label }}</span>
          </li>
        </ul>
      </div>
      <div v-if="props.footer" class="nx-chart-tooltip__footer">{{ props.footer }}</div>
    </div>
  </Teleport>
</template>

<style scoped>
.nx-chart-tooltip-anchor {
  position: absolute;
  width: 0;
  height: 0;
  pointer-events: none;
}
.nx-chart-tooltip {
  /* A popover's UA style is `inset: 0` and a centering margin; left/top place it. */
  inset: auto;
  margin: 0;
  z-index: var(--nxt-z-tooltip);
  min-width: calc(var(--nxt-size-unit) * 7);
  max-width: var(--nxt-panel-width-xs);
  padding: var(--nxt-space-xs) var(--nxt-space-sm);
  border: var(--nxt-hairline) solid var(--nxt-popover-border);
  border-radius: var(--nxt-radius-md);
  background: var(--nxt-popover-bg);
  color: var(--nxt-popover-fg);
  box-shadow: var(--nxt-popover-shadow);
  font-size: var(--nxt-font-size-xs);
  line-height: var(--nxt-line-height-tight);
  pointer-events: none;
  animation: nx-chart-tooltip-in var(--nxt-motion-fast) var(--nxt-ease-out);
}
.nx-chart-tooltip.is-columns {
  max-width: none;
}
.nx-chart-tooltip__columns {
  display: flex;
  align-items: flex-start;
  gap: var(--nxt-space-md);
}
.nx-chart-tooltip__title {
  margin-block-end: var(--nxt-space-2xs);
  color: var(--nxt-fg-subtle);
  font-weight: var(--nxt-font-weight-medium);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.nx-chart-tooltip__rows {
  display: grid;
  grid-template-columns: auto auto minmax(0, 1fr);
  align-items: center;
  gap: var(--nxt-space-2xs) var(--nxt-space-xs);
  margin: 0;
  padding: 0;
  font-size: inherit;
  list-style: none;
}
.nx-chart-tooltip__row {
  display: contents;
}
.nx-chart-tooltip__row.is-muted > * {
  opacity: 0.5;
}
.nx-chart-tooltip__key {
  grid-column: 1;
  width: calc(var(--nxt-dot-size-md) * 1.25);
  height: var(--nxt-dot-size-md);
  border-radius: calc(var(--nxt-hairline) * 2);
}
.nx-chart-tooltip__key.is-line {
  height: calc(var(--nxt-hairline) * 2.5);
  border-radius: var(--nxt-radius-full);
}
.nx-chart-tooltip__value {
  grid-column: 2;
  color: var(--nxt-fg);
  font-weight: var(--nxt-font-weight-semibold);
  font-variant-numeric: tabular-nums;
  text-align: end;
  white-space: nowrap;
}
.nx-chart-tooltip__label {
  grid-column: 3;
  color: var(--nxt-fg-subtle);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.nx-chart-tooltip__footer {
  margin-block-start: var(--nxt-space-xs);
  padding-block-start: var(--nxt-space-xs);
  border-block-start: var(--nxt-hairline) solid var(--nxt-border);
  color: var(--nxt-fg);
  font-weight: var(--nxt-font-weight-semibold);
  font-variant-numeric: tabular-nums;
}

@keyframes nx-chart-tooltip-in {
  from {
    opacity: 0;
  }
}
@media (prefers-reduced-motion: reduce) {
  .nx-chart-tooltip {
    animation: none;
  }
}
</style>
